import * as React from 'react';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useQuery } from 'react-apollo';

import { Size, useSize } from 'react-hook-size';
import nanoid from "nanoid"; 
import _ from "lodash";

import { Source, Layer, NavigationControl } from 'react-map-gl';
import MapGL from 'react-map-gl';
import { ViewState } from 'react-map-gl-draw/node_modules/@types/react-map-gl';
import { DrawPolygonMode, EditingMode, Editor } from 'react-map-gl-draw';

import ESRI_BASE from '../../../components/Map/styles/esri-base.json';
import ESRI_BASE_SATELLITE from '../../../components/Map/styles/esri-base-satellite.json';
import { MAPBOX_BASE_SATELLITE } from '../../../components/Map/styles/mapbox-base-satellite';
import ESRI_BASE_LABELS from '../../../components/Map/styles/esri-base-labels.json';
import NAIP from '../../../components/Map/styles/naip.json';
import { editFieldsLayer, fieldsBorderLayer, editFieldsBorderLayerFragments, editFieldsLayerFragments } from '../../../components/Map/styles/fields';
import { getEditHandleStyle, getFeatureStyle } from '../../../components/Map/styles/draw';

import EditFieldForm from './EditFieldForm';
import { Field, LayerVisibility, DataSourceLayerMeta, DataSourceRasterLayer} from '../../../types/types';
import { GET_FIELDS } from '../../../../../queries';
import { GetFields } from '../../../../../__generated__/types';
import { useRasterDataSources } from '../../../hooks/useRasterDataSources';

import { createFeatureCollection, createFeatureFromField, createPolygonFeatureFromField, createPolygonFeatureFromFieldMulti, getViewport, zoomToFeature } from '../../../utils/mapUtils';
import { generatePath } from 'react-router-dom';
import LayerVisibilityControlPanel from './LayerVisibilityControlPanel';
import { 
    HIGH_RES_COLOR
} from '../../../constants';
import { useStore } from '../../../providers/AppProvider';
import { getTileUrlTemplate } from "../../../utils/dataSourceUtils";
const navControlStyle = {
    left: 10,
    top: 10
};

const initLayerVisibility: LayerVisibility[] = [{
    layer_id: 'naip',
    layer_name: 'Historical Imagery',
    is_visible: false,
    order: 3,
    source: NAIP,
},{
    layer_id: 'MAPBOX',
    layer_name: 'Mapbox Imagery',
    is_visible: true,
    order: 4,
    source: MAPBOX_BASE_SATELLITE,
},{
    layer_id: 'ESRI_LABELS',
    layer_name: 'Labels',
    is_visible: true,
    order: 1,
    source: ESRI_BASE_LABELS,
}];

const FIELD_APP_URL = process.env.REACT_APP_FIELD_MGR_ROOT_URL;

export const EditField = () => {
    const history = useHistory();

    const mapContainerRef = useRef(null);
    const editorRef = useRef(null);

    const { vp, field } = useParams();

    const { width, height } = useSize(mapContainerRef);

    const [viewport, setViewport] = useState<ViewState>(getViewport(vp));

    const [fieldsDataSource, setFieldsDataSource] = useState(null);
    const [editFeature, setEditFeature] = useState<any>(null);
    const [editFeatureFragments, setEditFeatureFragments] = useState<any>(null);
    const [mode, setMode] = useState<DrawPolygonMode | EditingMode>(new DrawPolygonMode());
    const [selectedFeatureIndex, setSelectedFeatureIndex] = useState(null);
    const [editField, setEditField] = useState<Field | null>(null);
    const [fields, setFields] = useState<Field[]>([]);
    const [noTimelineLoaded, setNoTimelineLoaded] = useState<boolean>(false);
    const [overlayVisibility, setOverlayVisible] = useState<LayerVisibility[]>(initLayerVisibility);

    const { data: fieldData, refetch: refetchFieldData } = useQuery<GetFields>(GET_FIELDS);

    useEffect(() => {
        console.log("Z050724 refetchingFieldData");
        refetchFieldData();
    }, []);

    const [rasterDataSources, setRasterDataSources] = useState<DataSourceRasterLayer[]>([]);
    const { orgId } = useStore();
    const RASTER_DATA_SOURCES: DataSourceLayerMeta[] = [{
        id: `${orgId}${HIGH_RES_COLOR}`,
        disambiguate:'High Res. Normal Color',
        name:'UAS Imagery',
        type: 'base',
        order: 2,
        visible: true,
        minZoom: 0,
        maxZoom: 24
    }];
    //console.log("080823 RASTER_DATA_SOURCES: ", RASTER_DATA_SOURCES);
    const { dataSources, loading } = useRasterDataSources(RASTER_DATA_SOURCES);
    useEffect(() => {
        if (!loading && dataSources) {
            console.log("080823 dataSources", dataSources)
            const highResSources: DataSourceRasterLayer[] = dataSources.filter((d: DataSourceRasterLayer) => d.type == "base")

            setRasterDataSources(highResSources);
        }
    }, [dataSources, loading]);

    useEffect(() => {
        console.log("A061424 rasterDataSources", rasterDataSources);
        if (rasterDataSources.length > 0) {
            let uas = rasterDataSources[0];
            let uasSource = {
                id: uas.id,
                layer_id: uas.id,
                layer_name: uas.name,
                is_visible: uas.is_visible,
                order: uas.order,
                uuid: nanoid(8),
                source:{
                    source: uas.source,
                    utility_layer: true,
                    stac_address: uas.source.stac_address,
                    version: 8,
                    sources: {
                        "raster-tiles": {
                            "type": "raster",
                            "tiles": uas.tiles,
                            "tileSize": 256,
                            "attribution": uas.source.citation
                        }
                    },
                    "layers": [{
                            "id": "uas-tiles",
                            "type": "raster",
                            "source": "raster-tiles",
                            "minzoom": 0,//(uas.source.max_zoom_level && uas.source.max_zoom_level < 25) ? uas.source.max_zoom_level : 24,
                            "maxzoom": 24,//(uas.source.min_zoom_level && uas.source.min_zoom_level < 25) ? uas.source.min_zoom_level : 0,
                        }
                    ]
                }
            }
            console.log("A061424 setOverlayVisible > uasSource", uasSource);
            setOverlayVisible(prevVisibility => [...prevVisibility, uasSource]);
        }
    }, [rasterDataSources]);

    const updateRasterWithNoTimeline = (_layer: any,_datesAvailable:any ) => {
        console.log("080923 _layer", _layer);
        console.log("080923 overlayVisibility", overlayVisibility);
        console.log("080923 _datesAvailable", _datesAvailable);
        let _tiles = getTileUrlTemplate(
            _layer.source.source,
            _layer.source.stac_address!, 
            _layer.source.source.collection_id!,
            'footprint_id',
            "-01-01",
            _datesAvailable[0],
            _layer.source.source.date_display_resolution!,
            "template-singledate-p1",
            null,
        ) 
        console.log("B061424 updateRasterWithNoTimeline", _tiles);
        if (!noTimelineLoaded) {
            setOverlayVisible((prevVisibility:LayerVisibility[]) => {
                let _oldVisibility = prevVisibility.filter((v:LayerVisibility) => v.layer_id != _layer.id);
                let _newVisibility = prevVisibility.filter((v:LayerVisibility) => v.layer_id === _layer.id);
                console.log("080923 _newVisibility", _newVisibility);
                _newVisibility[0].source.sources["raster-tiles"].tiles = _tiles;
                _newVisibility[0]["uuid"] = nanoid(8);
                let _returnable = [..._oldVisibility, _newVisibility[0]] as LayerVisibility[];
                console.log("080923 _returnable", _returnable);
                console.log("080923 prevVisibility", prevVisibility);
                return _returnable;
            });
            setNoTimelineLoaded(true);
        }
    }
    
    useEffect(() => {
        console.log("C050724 editFeature", editFeature);
        console.log("C050724 editFeatureFragments", editFeatureFragments);
        console.log("C050724 fieldsDataSource", fieldsDataSource);
    }, [editFeatureFragments, editFeature, fieldsDataSource]);

    useEffect(() => {
        console.log("E050724 mode", mode)
    }, [mode]);

    useEffect(() => {
        console.log("Z050724 field", field);
        console.log("Z050724 fieldData", fieldData);
        if(fieldData) {
            const [fields, featureCollection] = createCollectionFromFields(fieldData, field);
            console.log("Z050724 fields", fields);
            console.log("Z050724 featureCollection", featureCollection);
            setFieldsDataSource(featureCollection);
            setFields(fields);
        }
    }, [fieldData, field]);

    const hasZoomedRef = useRef(false);

    useEffect(() => {
        console.log("Z050724 fields", fields);
        console.log("Z050724 field", field);
        hasZoomedRef.current = false;
        if(field && fields) {
            let editingField = fields.find(f => f.id == field);
            console.log("Z050724 editingField", editingField);
            if(editingField) {
                setEditField({...editingField});

                // editor needs an array of polygon features
                const fieldFeatureFrag = createPolygonFeatureFromFieldMulti(editingField);
                setEditFeatureFragments(fieldFeatureFrag);
                const fieldFeature = createPolygonFeatureFromField(editingField);
                setEditFeature([fieldFeature]);

                setMode(new EditingMode());
            }
        }
    }, [field, fields]);

    useEffect(() => {
        if(editFeature && editFeature[0] && width && height && !hasZoomedRef.current) {
            hasZoomedRef.current = true;
            setViewport(zoomToFeature(editFeature[0], viewport, width, height, 100));
        }
    }, [editFeature, width, height]);

    const createCollectionFromFields = (fieldData: GetFields, fieldId: string) => {
        const fields = fieldData.landscan_fields.map((f:any) => {
            return {...f, id: f.field_id } as Field;
        });

        // create features
        const features = fields.map((f:Field) => createFeatureFromField(f));

        // create a collection, exclude the edit field if one exists
        console.log("Z050624 features", features);
        const featureCollection = createFeatureCollection(field ? 
            features.filter((f: any) => f.properties.id != field) : features);
        console.log("Z050624 featureCollection", featureCollection);
        return [fields, featureCollection];
    }

    const onSelect = useCallback(options => {
        console.log('E050724 options', options);
        setSelectedFeatureIndex(options && options.selectedFeatureIndex);

        if (options && options.selectedFeatureIndex && options.selectedFeatureIndex >= 0) {
            setMode(new EditingMode());
        }/**/
    }, []);

    const onUpdate = useCallback((event) => {
        console.log('E050724 event', event);
        const { editType, data } = event;
        
        /*setEditFeature(data);*/
        setEditFeatureFragments(
            createFeatureCollection(data.map((f: any) => {
                return {
                    type: "Feature",
                    "properties": f.properties,
                    "geometry": f.geometry
                }
            }))
        )

        console.log(editType);

        if (editType === 'addFeature') {
            setMode(new EditingMode());
        }
    }, []);

    const getCursor = () => {
        return 'pointer';
    }
    
    const delayedUrlUpdate = React.useCallback(_.debounce((url:any) => history.replace(url), 250), []);

    const onInteractionStateChange = (e: any) => {
        if(!e.isDragging && !e.isPanning && !e.isZooming) {
            const str = `@${viewport.latitude.toFixed(5)},${viewport.longitude.toFixed(5)},${viewport.zoom.toFixed(2)}`;
            console.log("Z050724 editField", editField);
            const url = editField ? 
                generatePath(`${FIELD_APP_URL}/:vp/fields/:field/edit`, { vp: str, field: editField.id }) : // http://localhost:4000/field-manager/@38.46580,-121.22446,14.79/fields/703/edit
                generatePath(`${FIELD_APP_URL}/:vp/fields/new`, {vp: str});
    
            //delayedUrlUpdate(url);
        }
    }

    const memoLayerVisibilityControlPanel = useMemo(() => {
            return (<div style={{position: 'absolute', bottom:'unset', top:'10px', right:'10px'}}>
                <LayerVisibilityControlPanel
                    showDatePicker={false}
                    layers={overlayVisibility}
                    onChange={(visibility: LayerVisibility[]) => setOverlayVisible(visibility)}
                    updateRasterWithNoTimeline={updateRasterWithNoTimeline}
                    />
            </div>)
    }, [overlayVisibility]);

    const memoOverlayVisibilityMap = useMemo(() => {
        if (overlayVisibility && overlayVisibility.length > 0) { 
            return (
                overlayVisibility.map((layer: LayerVisibility) => (
                    console.log("080923 layer", layer),
                    <Source 
                        key={`s-${layer.uuid ? layer.uuid : layer.layer_id}`}
                        id={layer.uuid ? layer.uuid : layer.layer_id}
                        type='raster'
                        tileSize={256}
                        tiles={layer.source.sources['raster-tiles'].tiles}>
                        <Layer 
                            key={layer.layer_id} 
                            id={layer.layer_id} 
                            type='raster' 
                            source={layer.layer_id} 
                            maxzoom={layer.source.layers[0].maxzoom} 
                            minzoom={layer.source.layers[0].minzoom} 
                            layout={{ visibility: layer.is_visible ? 'visible' : 'none' }} 
                        />
                    </Source>
                    ))
                )
        } else {
            return null
        }
    }, [overlayVisibility]);

    return (
        <div className="d-flex h-100 position-relative">
            <div className="bg-light border border-bottom-0 border-dark border-top-0 d-flex flex-column flex-shrink-0 shadow-sm text-dark" style={{ width: '300px' }}>
               <EditFieldForm fieldToEdit={editField} feature={editFeature} featureFragments={editFeatureFragments}/>
            </div>
            <div className="flex-fill bg-white position-relative" ref={mapContainerRef}>
                <MapGL
                    {...viewport}
                    width='100%'
                    height='100%'
                    getCursor={() => getCursor()}
                    doubleClickZoom={false}
                    mapStyle={ESRI_BASE}
                    //onClick={onClick}
                    //onClick={(e:any) => onClick(e)}
                    onInteractionStateChange={(s:any) => onInteractionStateChange(s)} 
                    onViewportChange={(v:ViewState) => setViewport(v)}> 
                    {memoOverlayVisibilityMap}
                    <Source type="geojson" data={fieldsDataSource}>
                        <Layer {...editFieldsLayer} />
                        <Layer {...fieldsBorderLayer} />
                    </Source>
                    { true//editFeatureFragments && editFeatureFragments.features 
                    ? <Editor
                        // https://www.npmjs.com/package/react-map-gl-draw/v/0.18.5
                        ref={editorRef}
                        style={{ width: '100%', height: '100%' }}
                        clickRadius={12}
                        mode={mode}
                        onUpdate={onUpdate}
                        onSelect={onSelect}
                        features={editFeatureFragments?.features}
                        selectedFeatureIndex={selectedFeatureIndex}
                        //features={editFeature} // old strategy that only accepts first feature of multipart geojson feature
                        editHandleShape={'circle'}
                        featureStyle={getFeatureStyle}
                        editHandleStyle={getEditHandleStyle}
                    />
                    : null
                    }
                    <NavigationControl style={navControlStyle} showCompass={false} />
                </MapGL>
                {memoLayerVisibilityControlPanel}
            </div>
        </div>
    );
}
